
import React, { useState } from 'react';
import { X, Upload, ArrowRight, CheckCircle2, AlertTriangle, FileSpreadsheet, Save, Loader2, Database } from 'lucide-react';
import { Button } from '../Button';
import { supabase } from '../../services/supabaseClient';
import { Report, IntervalType } from '../../types';
import { resolveImportCoordinator } from './adminCsvImportLogic';
import { benachrichtigeKickoff } from '../../services/kickoff/kickoffService';
import { buildActorName } from '../../services/actorName';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentUser: any;
}

// Comprehensive list of fields available for import
const TARGET_FIELDS = [
    // Basis
    { key: 'projectNumber', label: 'Projektnummer (Pflicht)', required: true, group: 'Basis' },
    { key: 'projectName', label: 'Projektname (Pflicht)', required: true, group: 'Basis' },
    { key: 'address', label: 'Standort / Adresse', required: false, group: 'Basis' },
    { key: 'startDate', label: 'Startdatum (DD.MM.YYYY)', required: false, group: 'Zeitplan' },
    { key: 'endDate', label: 'Enddatum (DD.MM.YYYY)', required: false, group: 'Zeitplan' },
    { key: 'interval', label: 'Intervall (weekly, monthly...)', required: false, group: 'Zeitplan' },
    
    // Koordinator
    { key: 'coordinator', label: 'Koordinator Name', required: false, group: 'Koordinator' },
    { key: 'coordinatorEmail', label: 'Koordinator E-Mail', required: false, group: 'Koordinator' },
    { key: 'coordinatorPhone', label: 'Koordinator Telefon', required: false, group: 'Koordinator' },

    // Bauherr
    { key: 'clientName', label: 'Bauherr Name', required: false, group: 'Bauherr' },
    { key: 'clientAddress', label: 'Bauherr Anschrift', required: false, group: 'Bauherr' },
    { key: 'clientContact', label: 'Bauherr Ansprechpartner', required: false, group: 'Bauherr' },
    { key: 'clientPhone', label: 'Bauherr Telefon', required: false, group: 'Bauherr' },
    { key: 'clientEmail', label: 'Bauherr E-Mail', required: false, group: 'Bauherr' },

    // Auftraggeber. Bleibt leer, wenn die CSV keine Spalte dafuer hat: der
    // Bauherr wird NIE stillschweigend als Auftraggeber uebernommen (er ist
    // eine andere Partei, und der Auftraggeber traegt die Rechnung). Wer
    // beides in einer Person hat, hakt es im Zuordnungsschritt an.
    { key: 'customerName', label: 'Auftraggeber Name', required: false, group: 'Auftraggeber' },
    { key: 'customerAddress', label: 'Auftraggeber Anschrift', required: false, group: 'Auftraggeber' },
    { key: 'customerContact', label: 'Auftraggeber Ansprechpartner', required: false, group: 'Auftraggeber' },
    { key: 'customerPhone', label: 'Auftraggeber Telefon', required: false, group: 'Auftraggeber' },
    { key: 'customerEmail', label: 'Auftraggeber E-Mail', required: false, group: 'Auftraggeber' },
];

export const AdminCsvImportModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, currentUser }) => {
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [, setRawFile] = useState<File | null>(null);
    const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
    const [csvRows, setCsvRows] = useState<string[][]>([]);
    
    // Mapping State: TargetKey -> CSV Header Name
    const [mapping, setMapping] = useState<Record<string, string>>({});
    
    // Analysis State
    const [analyzedData, setAnalyzedData] = useState<any[]>([]);
    const [isChecking, setIsChecking] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [delimiter, setDelimiter] = useState(';');

    // Ausdrueckliche Ansage des Importierenden: Auftraggeber = Bauherr. Ohne
    // Haken bleiben die Auftraggeber-Felder leer, wenn die CSV keine haben.
    const [customerSameAsClient, setCustomerSameAsClient] = useState(false);

    if (!isOpen) return null;

    // --- STEP 1: PARSE CSV ---
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setRawFile(file);

        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target?.result as string;
            const lines = text.split('\n').filter(l => l.trim().length > 0);
            if (lines.length < 2) {
                alert("CSV muss mindestens eine Kopfzeile und eine Datenzeile enthalten.");
                return;
            }

            // Detect delimiter if not set manually
            let usedDelimiter = delimiter;
            if (lines[0].includes(';') && !lines[0].includes(',')) usedDelimiter = ';';
            if (lines[0].includes(',') && !lines[0].includes(';')) usedDelimiter = ',';
            setDelimiter(usedDelimiter);

            const headers = lines[0].split(usedDelimiter).map(h => h.trim().replace(/^"|"$/g, ''));
            const rows = lines.slice(1).map(line => {
                // Simple split (enhancement: regex for quoted CSV would be better for complex data)
                return line.split(usedDelimiter).map(cell => cell.trim().replace(/^"|"$/g, ''));
            });

            setCsvHeaders(headers);
            setCsvRows(rows);
            
            // Auto-Map attempt (Case insensitive, fuzzy match)
            const initialMap: Record<string, string> = {};
            TARGET_FIELDS.forEach(field => {
                const searchTerms = [field.label.toLowerCase(), field.key.toLowerCase()];
                // Common aliases
                if(field.key === 'address') searchTerms.push('straße', 'ort', 'plz');
                if(field.key === 'clientName') searchTerms.push('bauherr');
                
                const match = headers.find(h => searchTerms.some(term => h.toLowerCase().includes(term)));
                if (match) initialMap[field.key] = match;
            });
            setMapping(initialMap);
            setStep(2);
        };
        reader.readAsText(file, 'ISO-8859-1'); // Default for German Excel CSVs usually
    };

    // --- STEP 2: VALIDATE ---
    const handleCheckData = async () => {
        // Validate Mapping
        if (!mapping['projectNumber'] || !mapping['projectName']) {
            alert("Bitte ordnen Sie zumindest Projektnummer und Projektname zu.");
            return;
        }

        setIsChecking(true);
        
        const projectNumbersToCheck = new Set<string>();
        const previewData: any[] = [];

        csvRows.forEach((row, idx) => {
            // Helper to get mapped value
            const getVal = (key: string) => {
                const header = mapping[key];
                const colIdx = csvHeaders.indexOf(header);
                return colIdx !== -1 ? (row[colIdx] || '').trim() : '';
            };

            const pNum = getVal('projectNumber');
            const pName = getVal('projectName');

            if (pNum && pName) {
                projectNumbersToCheck.add(pNum);
                
                // Dynamically build the object based on all TARGET_FIELDS
                const rowData: any = {
                    originalIndex: idx,
                    status: 'pending'
                };
                
                TARGET_FIELDS.forEach(field => {
                    rowData[field.key] = getVal(field.key);
                });

                previewData.push(rowData);
            }
        });

        // Database Check
        try {
            const { data: existing } = await supabase
                .from('projects')
                .select('project_number')
                .in('project_number', Array.from(projectNumbersToCheck)); // Check SQL Column
            
            const existingSet = new Set(existing?.map(e => e.project_number) || []);

            const finalAnalysis = previewData.map(item => {
                if (existingSet.has(item.projectNumber)) {
                    return { ...item, status: 'duplicate', message: 'Existiert bereits (wird übersprungen)' };
                }
                return { ...item, status: 'new', message: 'Bereit zum Import' };
            });

            setAnalyzedData(finalAnalysis);
            setStep(3);

        } catch (e) {
            console.error(e);
            alert("Fehler bei der Datenbank-Prüfung.");
        } finally {
            setIsChecking(false);
        }
    };

    // --- STEP 3: IMPORT ---
    const handleExecuteImport = async () => {
        setIsImporting(true);
        const toImport = analyzedData.filter(d => d.status === 'new');
        
        if (toImport.length === 0) {
            alert("Keine neuen Projekte zum Importieren.");
            setIsImporting(false);
            return;
        }

        const parseDate = (dateStr: string) => {
            if (!dateStr) return undefined;
            
            // Handle DD.MM.YYYY or YYYY.MM.DD
            if (dateStr.includes('.')) {
                const parts = dateStr.split('.');
                if (parts.length === 3) {
                    if (parts[0].length === 4) return `${parts[0]}-${parts[1]}-${parts[2]}`; // YYYY.MM.DD
                    return `${parts[2]}-${parts[1]}-${parts[0]}`; // DD.MM.YYYY
                }
            }
            
            // Handle DD-MM-YYYY or YYYY-MM-DD
            if (dateStr.includes('-')) {
                const parts = dateStr.split('-');
                if (parts.length === 3) {
                    if (parts[0].length === 4) return dateStr; // YYYY-MM-DD
                    return `${parts[2]}-${parts[1]}-${parts[0]}`; // DD-MM-YYYY
                }
            }

            // Fallback for other formats
            const d = new Date(dateStr);
            if (!isNaN(d.getTime())) {
                return d.toISOString().split('T')[0];
            }
            
            return dateStr;
        };

        // Bauherr-Wert fuer ein Auftraggeber-Feld: nur mit ausdruecklichem Haken.
        const fromClient = (v?: string) => (customerSameAsClient ? (v || '') : '');

        try {
            const dbPayloads = toImport.map(item => {
                const projectId = crypto.randomUUID();

                // Koordinator der Zeile — die id nur, wenn die CSV den
                // Importierenden selbst benennt (siehe adminCsvImportLogic).
                const coordinator = resolveImportCoordinator(item, currentUser);

                // Parse Dates
                const isoStart = parseDate(item.startDate) || new Date().toISOString().split('T')[0];
                const isoEnd = parseDate(item.endDate);

                // Determine Interval
                let intervalConfig = { type: 'weekly' as IntervalType };
                if (item.interval) {
                    const i = item.interval.toLowerCase();
                    if (i.includes('month') || i.includes('monat')) intervalConfig.type = 'monthly';
                    else if (i.includes('daily') || i.includes('tag')) intervalConfig.type = 'daily';
                    else if (i.includes('bi') || i.includes('14')) intervalConfig.type = 'biweekly';
                }

                // Construct Report JSON with full details
                const newReport: Report = {
                    metadata: {
                        projectNumber: item.projectNumber,
                        projectName: item.projectName,
                        projectAddress: item.address,
                        inspectionNumber: 1,
                        date: new Date().toISOString(),
                        time: '08:00',
                        // Rollen-Literal, KEIN Name — cloudService/personName
                        // werten 'coordinator' ausdrücklich als „nicht gesetzt"
                        // und greifen daher auf den echten Nutzernamen durch.
                        // Von der Koordinator-Auflösung unberührt.
                        conductedBy: 'coordinator',

                        // Coordinator
                        coordinator: coordinator.name,
                        coordinatorId: coordinator.id,
                        coordinatorEmail: item.coordinatorEmail || '',
                        coordinatorPhone: item.coordinatorPhone || '',
                        
                        // Client
                        clientName: item.clientName || 'Unbekannt',
                        clientAddress: item.clientAddress || '',
                        clientContactPerson: item.clientContact || '',
                        clientPhone: item.clientPhone || '',
                        clientEmail: item.clientEmail || '',
                        
                        // Customer. Der Bauherr wird nur uebernommen, wenn der
                        // Importierende es angehakt hat — dann aber VOLLSTAENDIG,
                        // damit kein Mischsatz aus Bauherr-Name und leerem
                        // Kontakt entsteht.
                        customerName: item.customerName || fromClient(item.clientName),
                        customerAddress: item.customerAddress || fromClient(item.clientAddress),
                        customerContactPerson: item.customerContact || fromClient(item.clientContact),
                        customerPhone: item.customerPhone || fromClient(item.clientPhone),
                        customerEmail: item.customerEmail || fromClient(item.clientEmail),
                        
                        distributionList: [],
                        plans: [],
                        intervalConfig: intervalConfig,
                        globalData: {
                            projectId: projectId, 
                            status: 'active',
                            startDate: isoStart,
                            endDate: isoEnd,
                            services: [],
                            serviceLog: [],
                            notes: []
                        }
                    },
                    settings: {
                        companyName: currentUser?.company_name || '', 
                        companyAddress: currentUser?.company_address || '',
                        companyLogo: currentUser?.company_logo || '',
                        appMode: 'sigeko' 
                    },
                    items: [],
                    checklists: [],
                    mutualHazards: [],
                    safetyPoints: []
                };

                return {
                    id: projectId,
                    user_id: currentUser?.id,
                    project_number: item.projectNumber,
                    name: item.projectName,
                    address: item.address,
                    client_name: item.clientName,
                    // Dieselbe Auflösung wie in den Metadaten: die Projektspalte
                    // führte sonst einen leeren Koordinator, während im JSON der
                    // Importierende stand — zwei Namen für dasselbe Projekt.
                    coordinator_name: coordinator.name,
                    status: 'active',
                    start_date: isoStart,
                    end_date: isoEnd,
                    updated_at: new Date().toISOString(),
                    // created_at removed to fix schema cache error and use db default
                    data: newReport,
                    current_inspection_number: 1
                };
            });

            const { error } = await supabase.from('projects').insert(dbPayloads);
            if (error) throw error;

            // Projekt-Kickoff (docs/projekt-kickoff-plan.md §4.2a): EIN Aufruf
            // für alle neuen Projekte — benachrichtigeKickoff bündelt je
            // Empfänger zu einer Sammelmail und taktet den Versand. Die Ids
            // vergibt der Client selbst, und der Insert ist ein einziger
            // Befehl (ganz oder gar nicht): nach Erfolg stehen genau diese
            // Zeilen. Fire-and-forget — der Import ist fertig, ein Mailfehler
            // darf ihn nicht nachträglich als gescheitert melden.
            const erstellerName = buildActorName(currentUser) || undefined;
            void benachrichtigeKickoff(dbPayloads.map(p => p.id), { erstellerName }).catch(err =>
                console.warn('[AdminCsvImport] Kickoff-Mail nicht ausgelöst:', err));

            alert(`${dbPayloads.length} Projekte erfolgreich importiert!`);
            onSuccess();
            onClose();

        } catch (e: any) {
            console.error(e);
            alert("Fehler beim Import: " + e.message);
        } finally {
            setIsImporting(false);
        }
    };

    // Helper to render groups
    const renderFieldGroup = (groupName: string) => {
        const fields = TARGET_FIELDS.filter(f => f.group === groupName);
        if (fields.length === 0) return null;

        return (
            <div className="space-y-2 mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100">
                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 border-b border-slate-200 pb-1">{groupName}</h4>
                {fields.map(field => (
                    <div key={field.key} className="flex items-center justify-between">
                         <div className="flex-1">
                            <label className="text-xs font-medium text-slate-700 block mb-1">
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                            </label>
                        </div>
                        <ArrowRight className="w-3 h-3 text-slate-300 mx-2" />
                        <div className="flex-1">
                            <select 
                                value={mapping[field.key] || ''}
                                onChange={(e) => setMapping({...mapping, [field.key]: e.target.value})}
                                className={`w-full p-1.5 border rounded text-xs outline-none focus:ring-1 focus:ring-emerald-500 bg-white ${field.required && !mapping[field.key] ? 'border-red-300 bg-red-50' : 'border-slate-300'}`}
                            >
                                <option value="">-- Nicht zugeordnet --</option>
                                {csvHeaders.map(h => (
                                    <option key={h} value={h}>{h}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    Array.from(new Set(TARGET_FIELDS.map(f => f.group)));

    return (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh] overflow-hidden animate-scale-in">
                
                {/* Header */}
                <div className="bg-emerald-600 text-white p-6 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="font-bold text-xl flex items-center gap-2">
                            <FileSpreadsheet className="w-6 h-6 text-emerald-100" />
                            Projekt-Import (CSV)
                        </h3>
                        <p className="text-emerald-100 text-sm mt-1">Schritt {step} von 3: {step === 1 ? 'Datei laden' : step === 2 ? 'Felder zuweisen' : 'Vorschau & Import'}</p>
                    </div>
                    <button onClick={onClose} className="hover:bg-white/10 p-2 rounded-full transition-colors"><X className="w-6 h-6"/></button>
                </div>

                <div className="flex-1 overflow-y-auto p-8">
                    
                    {step === 1 && (
                        <div className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-slate-300 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer relative">
                            <input type="file" accept=".csv" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleFileUpload} />
                            <Upload className="w-12 h-12 text-slate-400 mb-4" />
                            <h4 className="text-lg font-bold text-slate-600">CSV-Datei hier ablegen</h4>
                            <p className="text-sm text-slate-400 mt-2">oder klicken zum Auswählen</p>
                            <div className="mt-6 flex gap-4">
                                <label className="flex items-center gap-2 text-sm text-slate-600 z-10 pointer-events-auto">
                                    <input type="radio" name="delim" checked={delimiter === ';'} onChange={() => setDelimiter(';')} className="text-emerald-600 focus:ring-emerald-500" />
                                    Semikolon (;)
                                </label>
                                <label className="flex items-center gap-2 text-sm text-slate-600 z-10 pointer-events-auto">
                                    <input type="radio" name="delim" checked={delimiter === ','} onChange={() => setDelimiter(',')} className="text-emerald-600 focus:ring-emerald-500" />
                                    Komma (,)
                                </label>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl text-blue-800 text-sm flex items-start gap-3">
                                <Database className="w-5 h-5 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-bold">Spalten-Zuordnung</p>
                                    <p className="opacity-80">Bitte ordnen Sie die Spalten Ihrer CSV-Datei den System-Feldern zu.</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="col-span-1 md:col-span-2">
                                    {renderFieldGroup('Basis')}
                                </div>
                                <div>
                                    {renderFieldGroup('Zeitplan')}
                                    {renderFieldGroup('Koordinator')}
                                </div>
                                <div>
                                    {renderFieldGroup('Bauherr')}
                                    {renderFieldGroup('Auftraggeber')}
                                    <label className="mt-3 flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={customerSameAsClient}
                                            onChange={(e) => setCustomerSameAsClient(e.target.checked)}
                                            className="mt-0.5 w-4 h-4 accent-emerald-600"
                                        />
                                        <span>
                                            <span className="font-bold">Auftraggeber ist der Bauherr</span>
                                            <span className="block text-xs text-slate-500">
                                                Nur ankreuzen, wenn das für ALLE Zeilen der Datei gilt. Ohne Haken
                                                bleibt der Auftraggeber leer, solange die CSV keine eigene Spalte hat.
                                            </span>
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6">
                             <div className="flex gap-4">
                                 <div className="flex-1 bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                                     <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600"><CheckCircle2 className="w-5 h-5"/></div>
                                     <div>
                                         <p className="text-2xl font-bold text-slate-800">{analyzedData.filter(d => d.status === 'new').length}</p>
                                         <p className="text-xs text-slate-500 uppercase font-bold">Neu (Importbereit)</p>
                                     </div>
                                 </div>
                                 <div className="flex-1 bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-center gap-3">
                                     <div className="p-2 bg-red-100 rounded-lg text-red-600"><AlertTriangle className="w-5 h-5"/></div>
                                     <div>
                                         <p className="text-2xl font-bold text-slate-800">{analyzedData.filter(d => d.status === 'duplicate').length}</p>
                                         <p className="text-xs text-slate-500 uppercase font-bold">Duplikate (Ignoriert)</p>
                                     </div>
                                 </div>
                             </div>

                             <div className="border border-slate-200 rounded-xl overflow-hidden max-h-[400px] overflow-y-auto">
                                 <table className="w-full text-left text-sm">
                                     <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs sticky top-0 shadow-sm">
                                         <tr>
                                             <th className="p-3">Status</th>
                                             <th className="p-3">Nr.</th>
                                             <th className="p-3">Name</th>
                                             <th className="p-3">Bauherr</th>
                                         </tr>
                                     </thead>
                                     <tbody className="divide-y divide-slate-100">
                                         {analyzedData.map((row, idx) => (
                                             <tr key={idx} className={row.status === 'duplicate' ? 'bg-red-50/50 opacity-60' : 'bg-white'}>
                                                 <td className="p-3">
                                                     {row.status === 'new' ? (
                                                         <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-xs"><CheckCircle2 className="w-3 h-3"/> Neu</span>
                                                     ) : (
                                                         <span className="inline-flex items-center gap-1 text-red-500 font-bold text-xs"><AlertTriangle className="w-3 h-3"/> Vorhanden</span>
                                                     )}
                                                 </td>
                                                 <td className="p-3 font-mono font-bold text-slate-700">{row.projectNumber}</td>
                                                 <td className="p-3 text-slate-600">{row.projectName}</td>
                                                 <td className="p-3 text-slate-500">{row.clientName || '-'}</td>
                                             </tr>
                                         ))}
                                     </tbody>
                                 </table>
                             </div>
                        </div>
                    )}

                </div>

                <div className="p-6 bg-slate-50 border-t border-slate-200 flex justify-between items-center shrink-0">
                    {step > 1 ? (
                        <button onClick={() => setStep(step === 2 ? 1 : 2)} className="text-slate-500 hover:text-slate-800 text-sm font-bold px-4 py-2">Zurück</button>
                    ) : (<div></div>)}
                    
                    {step === 1 && (
                        <span className="text-xs text-slate-400 italic">Bitte wählen Sie eine .csv Datei</span>
                    )}

                    {step === 2 && (
                        <Button variant="primary" onClick={handleCheckData} disabled={isChecking}>
                            {isChecking ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Database className="w-4 h-4 mr-2"/>}
                            {isChecking ? 'Prüfe Datenbank...' : 'Vorschau & Prüfen'}
                        </Button>
                    )}

                    {step === 3 && (
                        <Button variant="primary" onClick={handleExecuteImport} disabled={isImporting || analyzedData.filter(d => d.status === 'new').length === 0} className="bg-emerald-600 hover:bg-emerald-700">
                            {isImporting ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Save className="w-4 h-4 mr-2"/>}
                            {isImporting ? 'Importiere...' : 'Import Ausführen'}
                        </Button>
                    )}
                </div>

            </div>
        </div>
    );
};
